import { ModalNL } from "@components/modal/Modal"
import { useAppSelector } from "@redux/app/hooks"
import { getCurrentCompany } from "@redux/features/currentCompany/currentCompanySlice"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation, useMatch } from "@tanstack/react-router"
import { Button, LoadingCircle } from "@ui-lib/index"
import { common } from "@utils/common"
import { requestApi2 } from "@utils/http"
import { portalSocket } from "@utils/websocket/portalSocket"
import { seempSocket } from "@utils/websocket/seempSocket"
import { endpoints, SeempActionGroup } from "api"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MdAdd, MdOutlineAssessment } from "react-icons/md"
import { getHandlePlantCacheUpdated } from "./SeempContent"

export default function SeempNewActionCard({
	reportId,
	actionsTypeList,
	actionGroups,
}: {
	reportId: number
	actionsTypeList: string[]
	actionGroups: SeempActionGroup[]
}) {
	const { t } = useTranslation()
	const userData = useAppSelector(getUserData)
	const [selectedOpModeIds, setSelectedOpModeIds] = useState<number[]>()
	const [selectedActionGroup, setSelectedActionGroup] = useState<string>("")
	const [selectedAction, setSelectedAction] = useState<string>("")

	const actions = actionGroups
		.find((item) => item.label === selectedActionGroup)
		?.actionsDefinitions.filter((a) =>
			a.type !== "custom" ? actionsTypeList.includes(a.type) === false : true
		)

	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const currentLocation = useLocation().pathname

	const search = useMatch({
		from: currentLocation.includes("clients")
			? "/clients/$clientName/_sopPageLayout/seemp"
			: "/_sopPageLayout/seemp",
		shouldThrow: false,
	})?.search

	const paramSelectedVessel = search?.vessel ?? ""

	const currentCompany = useAppSelector(getCurrentCompany)

	const plantData = common.plantDataFromOwnerAndName.get(
		`${currentCompany?.toLowerCase()}/${paramSelectedVessel?.toLowerCase()}`
	)

	const [isAddActionModalOpen, setIsAddActionModalOpen] = useState(false)

	const handleAddingAction = async (
		actionDefinitionId: number | undefined,
		action: string,
		reportId: number,
		userEmail: string
	) => {
		const plantId = plantData?.id
		if (
			plantId === undefined ||
			currentCompany === undefined ||
			selectedAction === "" ||
			selectedActionGroup === "" ||
			actionDefinitionId === undefined
		) {
			return
		}

		setIsAddActionModalOpen(true)
		const newActionData = await requestApi2(endpoints.seempAddAction, {
			actionType: action,
			actionDefinitionId,
			reportId,
			createdBy: userEmail,
			socketId: portalSocket.getSocketId(),
		})

		if (newActionData == null) {
			alert(t("FAILED TO ADD ACTION"))
			setSelectedAction("")
			setSelectedActionGroup("")
			setSelectedOpModeIds(undefined)
			setIsAddActionModalOpen(false)

			return
		}
		seempSocket.sendInvalidatePlantCacheRequest(currentCompany, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			currentCompany,
			plantId,
			"add",
			newActionData,
			reportId,
			newActionData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "add",
			data: newActionData,
			reportId: reportId,
			actionId: newActionData.id, //actionData.id,
		})
		setIsAddActionModalOpen(false)
		setSelectedAction("")
		//setSelectedActionGroup("")
		//setSelectedOpModeIds(undefined)
	}

	return (
		<div className={`shadow-card flex w-full flex-col rounded-sm bg-white p-3`}>
			<p className={"text-dipai-secondary-901 text-menu h-8 font-semibold"}>
				{t("ADD NEW ACTION")}
			</p>
			<div className={`flex w-full flex-row justify-between gap-6`}>
				<div className={`flex flex-row gap-3`}>
					<div className={`flex flex-col gap-1.5`}>
						<label
							htmlFor="mode-select"
							className={`text-tiny h-4`}
						>
							{t("MODE")}
						</label>
						<div className={`relative`}>
							<MdOutlineAssessment
								className={`text-dipai-secondary-800 absolute top-2 ml-2`}
							/>
							<select
								id="mode-select"
								onChange={(e) => {
									const tempSelectedActionGroup =
										e.target.selectedOptions?.[0]?.label
									if (tempSelectedActionGroup === undefined) {
										return
									}
									setSelectedOpModeIds(
										e.target.value.split(",").map((v) => Number(v))
									)
									setSelectedActionGroup(tempSelectedActionGroup)
								}}
								value={selectedOpModeIds?.join(",") ?? ""}
								className={`border-gray-ccc text-small hover:border-dipai-secondary-500 focus:border-dipai-secondary-500 mx-px h-8 w-auto rounded-sm border-1 bg-transparent py-0.5 pl-8 hover:cursor-pointer focus:mx-0 focus:border-2 focus:ring-0 focus:outline-hidden`}
							>
								<option
									value=""
									disabled
								>
									{t("SELECT OPERATION MODE")}
								</option>
								{actionGroups.map((item, index) => {
									const modeName = item.label
									return (
										<option
											key={index}
											value={item.id}
										>
											{modeName}
										</option>
									)
								})}
							</select>
						</div>
					</div>
					<div className={`flex flex-col gap-1.5`}>
						<label
							htmlFor="action-select"
							className={`text-tiny h-4`}
						>
							{t("ACTION")}
						</label>
						<select
							id="action-select"
							className={`border-gray-ccc text-small hover:border-dipai-secondary-500 focus:border-dipai-secondary-500 mx-px h-8 w-auto rounded-sm border-1 bg-transparent py-0.5 hover:cursor-pointer focus:mx-0 focus:border-2 focus:ring-0 focus:outline-hidden`}
							onChange={(e) => setSelectedAction(e.target.value)}
							value={selectedAction}
						>
							<option
								value=""
								disabled
							>
								{t("SELECT ACTION")}
							</option>
							{actions?.map((action, index) => (
								<option
									key={index}
									value={action.type}
								>
									{t(
										action.type.includes(":")
											? action.type.split(":").slice(1).join(":").trimStart()
											: action.type
									)}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className={`flex items-end`}>
					<Button
						variant={"filled-secondary"}
						disabled={
							isAddActionModalOpen ||
							selectedAction === "" ||
							selectedActionGroup === ""
						}
						onClick={() =>
							handleAddingAction(
								actions?.find((a) => a.type === selectedAction)?.id,
								selectedAction,
								reportId,
								userData.email
							)
						}
					>
						<Button.Icon>{MdAdd}</Button.Icon>
						<Button.Label>{t("ADD")}</Button.Label>
					</Button>
				</div>
				<ModalNL
					// initialFocus={multiSelectRef}
					isOpen={isAddActionModalOpen}
					setIsOpen={setIsAddActionModalOpen}
					opacity={0.75}
				>
					<SeempAddActionPopup />
				</ModalNL>
			</div>
		</div>
	)
}

function SeempAddActionPopup() {
	const { t } = useTranslation()

	return (
		<div
			className={`fixed inset-1/2 flex h-80 w-80 -translate-1/2 flex-col items-center rounded-lg bg-white align-middle`}
		>
			<div
				className={`relative flex h-45 w-full items-center justify-center align-middle`}
			>
				<div className={`absolute w-full animate-pulse`} />
				<LoadingCircle
					size="medium"
					containerHeightWithUnit="180px"
				/>
			</div>
			<label className={`text-menu h-45 content-center text-center font-semibold`}>
				{t("ADDING ACTION, PLEASE WAIT...")}
			</label>

			<button
				tabIndex={0}
				style={{ opacity: 0, position: "absolute", pointerEvents: "none" }}
				aria-hidden="true"
			>
				Focusable
			</button>
		</div>
	)
}
